import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import './App.css';

type PageKey = 'inicio' | 'nosotros' | 'visitanos' | 'galeria';
type GalleryCategory = 'Adoración' | 'Comunidad' | 'Campamento' | 'Danza' | 'Liderazgo';

type GalleryItem = {
  src: string;
  alt: string;
  category: GalleryCategory;
  shape: 'wide' | 'tall' | 'standard';
};

const MAP_URL = 'https://maps.app.goo.gl/G6eNB6HSEWbdQkho9';
const INSTAGRAM_URL = 'https://www.instagram.com/fextremachurch/';
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61577774290195';
const NOTSIDE_URL = 'https://notside.com';
const EMAIL = 'fextremachurch@gmail.com';
const PHONE = '+18316132850';

const media = {
  logo: new URL('../public/fextrema-logo.png', import.meta.url).toString(),
  image1: new URL('../public/assets/image-1.jpg', import.meta.url).toString(),
  image2: new URL('../public/assets/image-2.jpg', import.meta.url).toString(),
  image3: new URL('../public/assets/image-3.jpg', import.meta.url).toString(),
  image4: new URL('../public/assets/image-4.jpg', import.meta.url).toString(),
  image5: new URL('../public/assets/image-5-pastors.jpg', import.meta.url).toString(),
  image6: new URL('../public/assets/image-6.jpg', import.meta.url).toString(),
  image7: new URL('../public/assets/image-7-danzarinas.jpg', import.meta.url).toString(),
  image8: new URL('../public/assets/image-8-danzarinas.jpg', import.meta.url).toString(),
  image9: new URL('../public/assets/image-9-danzarinas.jpg', import.meta.url).toString(),
  image10: new URL('../public/assets/image-10-camp.jpg', import.meta.url).toString(),
  image11: new URL('../public/assets/image-11-camp.jpg', import.meta.url).toString(),
  image12: new URL('../public/assets/image-12.jpg', import.meta.url).toString(),
  image13: new URL('../public/assets/image-13.jpg', import.meta.url).toString(),
  image14: new URL('../public/assets/image-14-adoracion.jpg', import.meta.url).toString(),
  image15: new URL('../public/assets/image-15.jpg', import.meta.url).toString(),
  image16: new URL('../public/assets/image-16.jpg', import.meta.url).toString(),
  image17: new URL('../public/assets/image-17.jpg', import.meta.url).toString(),
  image18: new URL('../public/assets/image-18.jpg', import.meta.url).toString(),
  image19: new URL('../public/assets/image-19-pastor-randy.jpg', import.meta.url).toString(),
  image20: new URL('../public/assets/image-20.jpg', import.meta.url).toString(),
};

const galleryItems: GalleryItem[] = [
  { src: media.image1, alt: 'La comunidad de Fe Extrema reunida en San José', category: 'Comunidad', shape: 'wide' },
  { src: media.image2, alt: 'Pastor Randy compartiendo un mensaje ante la congregación', category: 'Liderazgo', shape: 'tall' },
  { src: media.image3, alt: 'Predicación durante un encuentro de Fe Extrema', category: 'Liderazgo', shape: 'wide' },
  { src: media.image4, alt: 'Momento del encuentro semanal de Fe Extrema', category: 'Comunidad', shape: 'tall' },
  { src: media.image5, alt: 'Pastores Randy Montilla y Emelyn Gutiérrez', category: 'Liderazgo', shape: 'tall' },
  { src: media.image6, alt: 'Joven adorando con las manos levantadas', category: 'Adoración', shape: 'wide' },
  { src: media.image7, alt: 'Ministerio de danza durante una presentación', category: 'Danza', shape: 'wide' },
  { src: media.image8, alt: 'Expresión de adoración a través de la danza', category: 'Danza', shape: 'wide' },
  { src: media.image9, alt: 'Danzarinas de Fe Extrema en el escenario', category: 'Danza', shape: 'wide' },
  { src: media.image10, alt: 'La comunidad reunida alrededor de una fogata', category: 'Campamento', shape: 'wide' },
  { src: media.image11, alt: 'Encuentro comunitario durante el campamento', category: 'Campamento', shape: 'wide' },
  { src: media.image12, alt: 'Congregación participando en el encuentro', category: 'Comunidad', shape: 'tall' },
  { src: media.image13, alt: 'Momento cercano de fe y comunidad', category: 'Adoración', shape: 'tall' },
  { src: media.image14, alt: 'Cantante liderando un momento de adoración', category: 'Adoración', shape: 'tall' },
  { src: media.image15, alt: 'La iglesia adorando durante un servicio', category: 'Adoración', shape: 'standard' },
  { src: media.image16, alt: 'Pastor Randy ministrando junto a la comunidad', category: 'Liderazgo', shape: 'standard' },
  { src: media.image17, alt: 'Una expresión personal de adoración', category: 'Adoración', shape: 'tall' },
  { src: media.image18, alt: 'Congregación de Fe Extrema en adoración', category: 'Adoración', shape: 'wide' },
  { src: media.image19, alt: 'Pastor Randy Montilla predicando', category: 'Liderazgo', shape: 'tall' },
  { src: media.image20, alt: 'Encuentro de adoración en Fe Extrema', category: 'Adoración', shape: 'wide' },
];

function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return <span aria-hidden="true">{diagonal ? '↗' : '→'}</span>;
}

function Reveal({ children, className = '', delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          element.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${className}`} style={{ '--reveal-delay': `${delay}ms` } as React.CSSProperties}>
      {children}
    </div>
  );
}

function SiteHeader({ page }: { page: PageKey }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const links: { key: PageKey; label: string; href: string }[] = [
    { key: 'inicio', label: 'Inicio', href: '/' },
    { key: 'nosotros', label: 'Nosotros', href: '/nosotros/' },
    { key: 'visitanos', label: 'Visítanos', href: '/visitanos/' },
    { key: 'galeria', label: 'Galería', href: '/galeria/' },
  ];

  return (
    <header className={`site-header ${scrolled || menuOpen ? 'is-solid' : ''} ${menuOpen ? 'menu-active' : ''}`}>
      <a className="brand" href="/" aria-label="Fe Extrema Church, inicio">
        <img src={media.logo} alt="" width="54" height="54" />
        <span>FE EXTREMA</span>
      </a>

      <nav className={`main-nav ${menuOpen ? 'is-open' : ''}`} aria-label="Navegación principal">
        <div className="mobile-nav-label">Explora</div>
        {links.map((link, index) => (
          <a
            key={link.key}
            href={link.href}
            className={page === link.key ? 'is-active' : ''}
            aria-current={page === link.key ? 'page' : undefined}
            onClick={() => setMenuOpen(false)}
          >
            <span className="nav-index">0{index + 1}</span>
            {link.label}
          </a>
        ))}
        <a className="mobile-instagram" href={INSTAGRAM_URL} target="_blank" rel="noreferrer">
          @fextremachurch <Arrow diagonal />
        </a>
      </nav>

      <a className="header-cta" href={MAP_URL} target="_blank" rel="noreferrer">
        Cómo llegar <Arrow diagonal />
      </a>

      <button
        className={`menu-toggle ${menuOpen ? 'is-open' : ''}`}
        type="button"
        aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span />
        <span />
      </button>
    </header>
  );
}

function Marquee() {
  const phrase = 'FE • FAMILIA • PROPÓSITO • JESÚS • ';
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">
        <span>{phrase.repeat(3)}</span>
        <span>{phrase.repeat(3)}</span>
      </div>
    </div>
  );
}

function ServiceStrip() {
  return (
    <section className="service-strip" aria-label="Horarios de encuentros">
      <div className="service-kicker"><span className="pulse-dot" /> Nos encontramos</div>
      <div className="service-time"><strong>MIÉRCOLES</strong><span>7:30 PM</span></div>
      <div className="service-time"><strong>DOMINGOS</strong><span>5:30 PM</span></div>
      <a href={MAP_URL} target="_blank" rel="noreferrer">San José, CA <Arrow diagonal /></a>
    </section>
  );
}

function ValuesSection({ light = false }: { light?: boolean }) {
  const values = [
    { number: '01', title: 'FE', text: 'Creemos más allá de lo que vemos y caminamos confiando en Jesús.' },
    { number: '02', title: 'FAMILIA', text: 'Somos una comunidad donde cada persona encuentra un lugar para crecer.' },
    { number: '03', title: 'PROPÓSITO', text: 'Vivimos una fe activa que transforma nuestra manera de servir y amar.' },
  ];
  return (
    <section className={`values-section ${light ? 'is-light' : ''}`}>
      <div className="section-shell">
        <Reveal className="section-heading split-heading">
          <div>
            <span className="eyebrow">Lo que nos mueve</span>
            <h2>UNA VIDA<br /><em>CON SENTIDO.</em></h2>
          </div>
          <p>Más que un lugar al que asistir, Fe Extrema es una comunidad para caminar juntos, crecer en la fe y vivir con propósito.</p>
        </Reveal>
        <div className="values-grid">
          {values.map((value, index) => (
            <Reveal className="value-card" delay={index * 90} key={value.title}>
              <span>{value.number}</span>
              <h3>{value.title}</h3>
              <p>{value.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PastorsSection({ cream = false }: { cream?: boolean }) {
  return (
    <section className={`pastors-section ${cream ? 'is-cream' : ''}`}>
      <div className="pastors-layout section-shell">
        <Reveal className="pastors-image-wrap">
          <img src={media.image5} alt="Pastores Randy Montilla y Emelyn Gutiérrez compartiendo juntos" width="1152" height="2048" loading="lazy" />
          <span className="photo-tag">SAN JOSE, CA</span>
        </Reveal>
        <Reveal className="pastors-copy" delay={100}>
          <span className="eyebrow">Nuestro liderazgo</span>
          <h2>PASTORES<br /><em>RANDY &amp; EMELYN</em></h2>
          <p className="lead">Randy Montilla y Emelyn Gutiérrez sirven juntos a Fe Extrema con un corazón enfocado en Jesús, las personas y una fe que se demuestra en la vida diaria.</p>
          <p>Su liderazgo invita a cada persona a acercarse a Dios, encontrar comunidad y descubrir una vida con propósito.</p>
          <a className="text-link" href="/nosotros/">Conoce nuestra comunidad <Arrow /></a>
        </Reveal>
      </div>
    </section>
  );
}

function VisitBanner() {
  return (
    <section className="visit-banner">
      <div className="visit-banner-image">
        <img src={media.image18} alt="La comunidad de Fe Extrema adorando unida" width="2048" height="1151" loading="lazy" />
      </div>
      <div className="visit-banner-copy">
        <span className="eyebrow dark">Tu lugar está aquí</span>
        <h2>VEN COMO<br />ERES.</h2>
        <p>133 Bernal Rd<br />San Jose, CA 95119</p>
        <a className="button button-dark" href={MAP_URL} target="_blank" rel="noreferrer">Cómo llegar <Arrow diagonal /></a>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-top section-shell">
        <span className="eyebrow">Nos vemos pronto</span>
        <h2>NOS VEMOS<br /><em>ESTE DOMINGO.</em></h2>
        <a className="round-link" href={MAP_URL} target="_blank" rel="noreferrer" aria-label="Abrir ubicación en Google Maps">
          <Arrow diagonal />
        </a>
      </div>
      <div className="footer-grid section-shell">
        <div>
          <img src={media.logo} alt="Fe Extrema Ministerio Internacional" width="88" height="88" />
        </div>
        <div><span>Encuentros</span><p>Miércoles · 7:30 PM<br />Domingos · 5:30 PM</p></div>
        <div><span>Ubicación</span><p>133 Bernal Rd<br />San Jose, CA 95119</p></div>
        <div className="footer-links">
          <span>Conecta</span>
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer">Instagram <Arrow diagonal /></a>
          <a href={FACEBOOK_URL} target="_blank" rel="noreferrer">Facebook <Arrow diagonal /></a>
        </div>
        <div className="footer-links">
          <span>Contacto</span>
          <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          <a href={`tel:${PHONE}`}>+1 831-613-2850</a>
        </div>
      </div>
      <div className="footer-bottom section-shell">
        <span>© 2026 Fe Extrema Ministerio Internacional</span>
        <span>Una comunidad de fe global</span>
        <span className="footer-credit">Diseñado por <a href={NOTSIDE_URL} target="_blank" rel="noreferrer">Notside <Arrow diagonal /></a></span>
      </div>
    </footer>
  );
}

function HomePage() {
  return (
    <>
      <section className="home-hero">
        <img className="hero-media" src={media.image15} alt="Congregación de Fe Extrema durante un encuentro de adoración" width="1800" height="1202" fetchPriority="high" />
        <div className="hero-overlay" />
        <div className="hero-content section-shell">
          <div className="hero-kicker">Comunidad de fe global <span>•</span> San Jose, CA</div>
          <h1><span>UNA FE</span><span>QUE SE <em>VIVE.</em></span></h1>
          <p>Una comunidad apasionada por Jesús, su presencia y una fe que transforma vidas.</p>
          <div className="hero-actions">
            <a className="button button-primary" href={MAP_URL} target="_blank" rel="noreferrer">Cómo llegar <Arrow diagonal /></a>
            <a className="button button-ghost" href="/nosotros/">Conócenos <Arrow /></a>
          </div>
        </div>
        <div className="scroll-cue"><span /> Desliza para descubrir</div>
      </section>
      <ServiceStrip />
      <Marquee />

      <section className="intro-section">
        <div className="section-shell intro-grid">
          <Reveal className="intro-title">
            <span className="eyebrow">Esto es Fe Extrema</span>
            <h2>UNA COMUNIDAD<br /><em>VIVA.</em></h2>
          </Reveal>
          <Reveal className="intro-copy" delay={100}>
            <p className="lead">Un lugar para encontrarte con Dios y con personas que caminan en la misma dirección.</p>
            <p>Somos una comunidad de fe global con casa en San José, California. Creemos en una iglesia cercana, apasionada y comprometida con vivir lo que cree.</p>
            <a className="text-link" href="/nosotros/">Nuestra historia <Arrow /></a>
          </Reveal>
        </div>
        <div className="editorial-collage section-shell">
          <Reveal className="collage-main"><img src={media.image6} alt="Momento personal de adoración en Fe Extrema" width="2048" height="1152" loading="lazy" /></Reveal>
          <Reveal className="collage-side" delay={120}><img src={media.image3} alt="Mensaje compartido durante un encuentro" width="2048" height="1152" loading="lazy" /></Reveal>
          <div className="collage-word" aria-hidden="true">PRESENCIA</div>
        </div>
      </section>

      <ValuesSection light />
      <PastorsSection />

      <section className="gallery-preview">
        <div className="section-shell">
          <Reveal className="section-heading gallery-heading">
            <div><span className="eyebrow">Momentos que nos marcan</span><h2>LO QUE VIVIMOS<br /><em>JUNTOS.</em></h2></div>
            <a className="button button-ghost" href="/galeria/">Ver galería <Arrow /></a>
          </Reveal>
          <div className="preview-grid">
            {[
              { src: media.image14, alt: 'Adoración en Fe Extrema', cls: 'preview-tall' },
              { src: media.image10, alt: 'Comunidad alrededor de una fogata', cls: 'preview-wide' },
              { src: media.image7, alt: 'Ministerio de danza', cls: 'preview-wide' },
            ].map((image, index) => (
              <Reveal key={image.src} className={image.cls} delay={index * 80}>
                <img src={image.src} alt={image.alt} loading="lazy" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <VisitBanner />
    </>
  );
}

function PageHero({ label, title, accent, copy, image }: { label: string; title: string; accent: string; copy: string; image: string }) {
  return (
    <section className="page-hero">
      <img className="hero-media" src={image} alt="" fetchPriority="high" />
      <div className="hero-overlay" />
      <div className="page-hero-content section-shell">
        <span className="hero-kicker">{label}</span>
        <h1>{title}<br /><em>{accent}</em></h1>
        <p>{copy}</p>
      </div>
    </section>
  );
}

function AboutPage() {
  return (
    <>
      <PageHero label="Quiénes somos" title="FE QUE SE" accent="CONVIERTE EN VIDA." copy="Somos una comunidad de fe global que encuentra su centro en Jesús y su expresión en las personas." image={media.image20} />
      <section className="manifesto-section">
        <div className="section-shell manifesto-grid">
          <Reveal><span className="eyebrow">Nuestro corazón</span><h2>NO CAMINAMOS<br /><em>SOLOS.</em></h2></Reveal>
          <Reveal className="manifesto-copy" delay={100}>
            <p className="lead">Fe Extrema es una casa para quienes desean conocer más de Dios, fortalecer su fe y construir relaciones reales.</p>
            <p>Nos reunimos para adorar, aprender y crecer juntos. Cada encuentro es una invitación a experimentar la presencia de Jesús y llevar esa fe a la vida cotidiana.</p>
          </Reveal>
        </div>
        <Reveal className="full-bleed-photo">
          <img src={media.image1} alt="La comunidad de Fe Extrema reunida" width="2048" height="1152" loading="lazy" />
          <span>UNA CASA · MUCHAS HISTORIAS</span>
        </Reveal>
      </section>
      <ValuesSection light />
      <PastorsSection />
      <section className="global-section">
        <div className="section-shell global-layout">
          <Reveal className="global-photo"><img src={media.image10} alt="Comunidad de Fe Extrema compartiendo alrededor de una fogata" width="1800" height="1013" loading="lazy" /></Reveal>
          <Reveal className="global-copy" delay={100}>
            <span className="eyebrow">Comunidad de fe global</span>
            <h2>UNA FE SIN<br /><em>FRONTERAS.</em></h2>
            <p>Nuestra casa está en San José, pero nuestro corazón es global: personas, generaciones e historias diferentes unidas por una misma fe.</p>
            <a className="button button-primary" href="/visitanos/">Visítanos <Arrow /></a>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function VisitPage() {
  return (
    <>
      <PageHero label="Planea tu visita" title="HAY UN LUGAR" accent="PARA TI." copy="Queremos que tu primera visita sea sencilla. Aquí tienes lo esencial para acompañarnos." image={media.image1} />
      <section className="schedule-section">
        <div className="section-shell">
          <Reveal className="section-heading split-heading">
            <div><span className="eyebrow">Nuestros encuentros</span><h2>EL MEJOR MOMENTO<br /><em>DE LA SEMANA.</em></h2></div>
            <p>Dos oportunidades cada semana para adorar, escuchar un mensaje y compartir en comunidad.</p>
          </Reveal>
          <div className="schedule-grid">
            <Reveal className="schedule-card">
              <span>01</span><div><small>ENTRE SEMANA</small><h3>MIÉRCOLES</h3><strong>7:30 PM</strong></div>
            </Reveal>
            <Reveal className="schedule-card is-featured" delay={90}>
              <span>02</span><div><small>ENCUENTRO DOMINICAL</small><h3>DOMINGOS</h3><strong>5:30 PM</strong></div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="location-section">
        <div className="section-shell location-layout">
          <Reveal className="location-card">
            <span className="eyebrow dark">Encuéntranos</span>
            <h2>133 BERNAL RD<br />SAN JOSE, CA<br /><em>95119</em></h2>
            <p>Abre la ubicación en Google Maps para ver la mejor ruta desde donde estés.</p>
            <a className="button button-dark" href={MAP_URL} target="_blank" rel="noreferrer">Abrir en Maps <Arrow diagonal /></a>
          </Reveal>
          <Reveal className="location-image" delay={100}>
            <img src={media.image12} alt="La congregación de Fe Extrema en San José" width="1350" height="1800" loading="lazy" />
            <span className="map-pin" aria-hidden="true">FE</span>
          </Reveal>
        </div>
      </section>

      <section className="expect-section">
        <div className="section-shell">
          <Reveal><span className="eyebrow">Tu primera vez</span><h2>¿QUÉ PUEDES<br /><em>ESPERAR?</em></h2></Reveal>
          <div className="expect-grid">
            {[
              ['01', 'ADORACIÓN', 'Un tiempo para acercarnos a Dios con libertad y gratitud.'],
              ['02', 'UN MENSAJE', 'Una enseñanza centrada en la fe y conectada con la vida diaria.'],
              ['03', 'COMUNIDAD', 'Personas reales caminando juntas y una bienvenida sincera.'],
            ].map(([number, title, text], index) => (
              <Reveal className="expect-item" delay={index * 90} key={title}>
                <span>{number}</span><h3>{title}</h3><p>{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <VisitBanner />
    </>
  );
}

function GalleryPage() {
  const [category, setCategory] = useState<'Todos' | GalleryCategory>('Todos');
  const [selected, setSelected] = useState<number | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchStart = useRef<number | null>(null);

  const filteredItems = useMemo(
    () => category === 'Todos' ? galleryItems : galleryItems.filter((item) => item.category === category),
    [category],
  );
  const categories: ('Todos' | GalleryCategory)[] = ['Todos', 'Adoración', 'Comunidad', 'Campamento', 'Danza', 'Liderazgo'];

  const closeLightbox = () => setSelected(null);
  const goPrevious = () => setSelected((current) => current === null ? null : (current - 1 + filteredItems.length) % filteredItems.length);
  const goNext = () => setSelected((current) => current === null ? null : (current + 1) % filteredItems.length);

  useEffect(() => {
    if (selected === null) return;
    document.body.classList.add('lightbox-open');
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') goPrevious();
      if (event.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('lightbox-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selected, filteredItems.length]);

  useEffect(() => setSelected(null), [category]);

  const onPointerDown = (event: React.PointerEvent) => { touchStart.current = event.clientX; };
  const onPointerUp = (event: React.PointerEvent) => {
    if (touchStart.current === null) return;
    const delta = event.clientX - touchStart.current;
    if (Math.abs(delta) > 50) delta > 0 ? goPrevious() : goNext();
    touchStart.current = null;
  };

  return (
    <>
      <PageHero label="Nuestra comunidad" title="LO QUE VIVIMOS" accent="JUNTOS." copy="Momentos de fe, adoración y comunidad. Historias reales de una iglesia viva." image={media.image18} />
      <section className="gallery-section">
        <div className="section-shell">
          <div className="gallery-toolbar" role="group" aria-label="Filtrar galería">
            {categories.map((item) => (
              <button key={item} type="button" className={category === item ? 'is-active' : ''} onClick={() => setCategory(item)} aria-pressed={category === item}>
                {item}
              </button>
            ))}
          </div>
          <div className="gallery-grid">
            {filteredItems.map((item, index) => (
              <button
                className={`gallery-item ${item.shape}`}
                key={item.src}
                type="button"
                onClick={() => setSelected(index)}
                aria-label={`Ampliar: ${item.alt}`}
              >
                <img src={item.src} alt={item.alt} loading={index < 4 ? 'eager' : 'lazy'} />
                <span className="gallery-meta"><small>{item.category}</small><b>VER <Arrow diagonal /></b></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {selected !== null && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="Visor de fotografías" onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
          <div className="lightbox-top">
            <span>{String(selected + 1).padStart(2, '0')} / {String(filteredItems.length).padStart(2, '0')}</span>
            <button ref={closeRef} type="button" onClick={closeLightbox} aria-label="Cerrar visor">CERRAR ×</button>
          </div>
          <button className="lightbox-nav prev" type="button" onClick={goPrevious} aria-label="Fotografía anterior">←</button>
          <figure>
            <img src={filteredItems[selected].src} alt={filteredItems[selected].alt} />
            <figcaption><span>{filteredItems[selected].category}</span>{filteredItems[selected].alt}</figcaption>
          </figure>
          <button className="lightbox-nav next" type="button" onClick={goNext} aria-label="Fotografía siguiente">→</button>
        </div>
      )}
    </>
  );
}

export function App() {
  const page = (document.body.dataset.page || 'inicio') as PageKey;
  const content = page === 'nosotros' ? <AboutPage /> : page === 'visitanos' ? <VisitPage /> : page === 'galeria' ? <GalleryPage /> : <HomePage />;

  return (
    <>
      <a className="skip-link" href="#contenido">Saltar al contenido</a>
      <SiteHeader page={page} />
      <main id="contenido">{content}</main>
      <SiteFooter />
    </>
  );
}
